import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, TrashIcon, CurrencyDollarIcon, PencilIcon, ExclamationCircleIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import { retailSaleApi, RetailSale, sellerApi, Seller } from '../../utils/retailSalesApi';
import { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PaymentDepositModal from './PaymentDepositModal';

const RetailSalesList: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [sales, setSales] = useState<RetailSale[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [depositModalSale, setDepositModalSale] = useState<RetailSale | null>(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [saleTypeFilter, setSaleTypeFilter] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sellerFilter, setSellerFilter] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'fish') {
      loadSellers();
      loadSales();
    }
  }, [selectedLocation?.id, businessType, statusFilter, saleTypeFilter, startDate, endDate, sellerFilter]);

  const loadSellers = async () => {
    if (!selectedLocation?.id) return;
    try {
      const data = await sellerApi.list(selectedLocation.id);
      setSellers(data);
    } catch (err: any) {
      console.error('Failed to load sellers', err);
    }
  };

  const loadSales = async () => {
    if (!selectedLocation?.id) return;
    
    try {
      setLoading(true);
      setError('');
      const params: any = {
        location_id: selectedLocation.id,
      };
      
      if (statusFilter) params.status = statusFilter;
      if (saleTypeFilter) params.sale_type = saleTypeFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (sellerFilter) params.seller_id = sellerFilter;
      
      const data = await retailSaleApi.list(params);
      setSales(data);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load sales'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (saleId: string, saleNumber: string) => {
    if (!window.confirm(`Are you sure you want to discard draft sale "${saleNumber}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(saleId);
      await retailSaleApi.delete(saleId);
      await loadSales();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to discard draft sale'));
    } finally {
      setDeletingId(null);
    }
  };

  const getDisplayAmount = React.useCallback((sale: RetailSale) => {
    if (sale.sale_type === 'on_farm') {
      if (sale.status === 'draft' && sale.cartons && sale.cartons.length > 0) {
        return sale.cartons.reduce((sum, carton) => {
          const qty = Number(carton.quantity_kg) || 0;
          const rate = Number(carton.rate_per_kg) || 0;
          return sum + qty * rate;
        }, 0);
      }
      return sale.total_amount || 0;
    }
    return sale.net_amount || sale.total_amount || 0;
  }, []);

  // Separate draft sales from completed sales and apply client-side amount filters
  const { draftSales, completedSales, filteredSales } = useMemo(() => {
    let filtered = sales;
    if (minAmount) {
      filtered = filtered.filter(s => getDisplayAmount(s) >= parseFloat(minAmount));
    }
    if (maxAmount) {
      filtered = filtered.filter(s => getDisplayAmount(s) <= parseFloat(maxAmount));
    }
    
    const drafts = filtered.filter(s => s.status === 'draft');
    const completed = filtered.filter(s => s.status !== 'draft');
    return { draftSales: drafts, completedSales: completed, filteredSales: filtered };
  }, [sales, minAmount, maxAmount, getDisplayAmount]);

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; bg: string }> = {
      draft: { color: 'text-orange-700', bg: 'bg-orange-100' },
      completed: { color: 'text-blue-700', bg: 'bg-blue-100' },
      deposit: { color: 'text-green-700', bg: 'bg-green-100' },
      cancelled: { color: 'text-red-700', bg: 'bg-red-100' },
    };
    
    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleContinueDraft = (sale: RetailSale) => {
    // Navigate to form with draftId in URL - form will load from server
    if (sale.sale_type === 'on_farm') {
      navigate(`/retail-sales/on-farm?draftId=${sale.id}`);
    } else {
      navigate(`/retail-sales/market?draftId=${sale.id}`);
    }
  };

  const canEdit = (sale: RetailSale) => {
    if (user?.role === 'admin') return true;
    if (sale.status === 'cancelled') return false;
    
    // Check 7-day edit window
    const saleDate = new Date(sale.sale_date);
    const daysOld = (Date.now() - saleDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysOld <= 7;
  };

  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for fish farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/retail-sales" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">All Sales</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Link to="/retail-sales/on-farm" className="btn-primary">
            On-Farm Sale
          </Link>
          <Link to="/retail-sales/market" className="btn-primary">
            Market Sale
          </Link>
        </div>
      </div>

      {/* Filters - Collapsible Accordion */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-600" />
            <span className="font-medium text-gray-900">Filters</span>
            {(statusFilter || saleTypeFilter || startDate || endDate || sellerFilter || minAmount || maxAmount) && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                Active
              </span>
            )}
          </div>
          {filtersCollapsed ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
        
        {!filtersCollapsed && (
          <div className="px-4 pb-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-4">
              <div>
                <label className="form-label text-xs">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="deposit">Deposit</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="form-label text-xs">Sale Type</label>
                <select
                  value={saleTypeFilter}
                  onChange={(e) => setSaleTypeFilter(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="">All Types</option>
                  <option value="on_farm">On-Farm</option>
                  <option value="market">Market</option>
                </select>
              </div>
              <div>
                <label className="form-label text-xs">Seller</label>
                <select
                  value={sellerFilter}
                  onChange={(e) => setSellerFilter(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="">All Sellers</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.mobile})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label text-xs">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input text-sm"
                />
              </div>
              <div>
                <label className="form-label text-xs">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input text-sm"
                />
              </div>
              <div>
                <label className="form-label text-xs">Min Amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className="form-input text-sm"
                />
              </div>
              <div>
                <label className="form-label text-xs">Max Amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  className="form-input text-sm"
                />
              </div>
              <div className="flex items-end">
                <button 
                  onClick={() => {
                    setStatusFilter('');
                    setSaleTypeFilter('');
                    setStartDate('');
                    setEndDate('');
                    setSellerFilter('');
                    setMinAmount('');
                    setMaxAmount('');
                  }}
                  className="w-full py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Draft Sales Section - Prominently displayed */}
      {draftSales.length > 0 && (
        <div className="card p-4 sm:p-6 border-2 border-orange-200 bg-orange-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
            <div className="flex items-center space-x-2 flex-wrap">
              <ExclamationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 flex-shrink-0" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                Draft Sales - Continue from here ({draftSales.length})
              </h2>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            You have {draftSales.length} incomplete sale{draftSales.length !== 1 ? 's' : ''}. Click "Continue" to resume editing.
          </p>
          <div className="space-y-3">
            {draftSales.map((sale) => (
              <div key={sale.id} className="bg-white rounded-lg p-4 border border-orange-200">
                <div className="flex flex-col gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900 text-sm break-words">{sale.retail_sale_number}</span>
                      {getStatusBadge(sale.status)}
                      <span className="text-xs sm:text-sm text-gray-600 capitalize">
                        {sale.sale_type === 'on_farm' ? 'On-Farm' : 'Market'}
                      </span>
                    </div>
                    <div className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                      <span className="font-medium">Seller:</span> {sale.seller_name || 'N/A'}
                      {sale.seller_mobile && <span className="ml-1">({sale.seller_mobile})</span>}
                    </div>
                    <div className="mt-1 text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">Date:</span> {new Date(sale.sale_date).toLocaleDateString()}
                      <span className="ml-2 sm:ml-4 font-medium">Amount:</span> ₹{getDisplayAmount(sale).toFixed(2)}
                    </div>
                    {sale.cartons && sale.cartons.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        {sale.cartons.length} fish crate{sale.cartons.length !== 1 ? 's' : ''} added
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handleContinueDraft(sale)}
                      className="btn-primary flex items-center justify-center space-x-2 flex-1 min-w-[100px]"
                      title="Continue Editing"
                    >
                      <PencilIcon className="h-4 w-4" />
                      <span className="text-xs sm:text-sm">Continue</span>
                    </button>
                    <button
                      onClick={() => navigate(`/retail-sales/${sale.id}`)}
                      className="text-blue-600 hover:text-blue-800 p-2 flex-shrink-0"
                      title="View Details"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(sale.id, sale.retail_sale_number)}
                      disabled={deletingId === sale.id}
                      className="text-red-600 hover:text-red-800 p-2 disabled:opacity-50 flex-shrink-0"
                      title="Discard Draft"
                    >
                      {deletingId === sale.id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <XMarkIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Sales List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          All Sales ({filteredSales.length})
          {draftSales.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({completedSales.length} completed)
            </span>
          )}
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : filteredSales.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {filteredSales.map((sale) => (
                <div 
                  key={sale.id} 
                  className={`bg-white rounded-lg p-4 border ${sale.status === 'draft' ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900 text-sm break-words">{sale.retail_sale_number}</span>
                        {getStatusBadge(sale.status)}
                        <span className="text-xs text-gray-600 capitalize">
                          {sale.sale_type === 'on_farm' ? 'On-Farm' : 'Market'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 break-words">
                        <span className="font-medium">Date:</span> {new Date(sale.sale_date).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-600 break-words mt-1">
                        <span className="font-medium">Seller:</span> {sale.seller_name || 'N/A'}
                        {sale.seller_mobile && <span className="ml-1">({sale.seller_mobile})</span>}
                      </div>
                      <div className="text-sm font-medium text-gray-900 mt-2">
                        Amount: ₹{getDisplayAmount(sale).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-gray-200">
                    {sale.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleContinueDraft(sale)}
                          className="text-orange-600 hover:text-orange-800 flex items-center space-x-1 text-xs"
                          title="Continue Editing"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span>Continue</span>
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id, sale.retail_sale_number)}
                          disabled={deletingId === sale.id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50 flex items-center space-x-1 text-xs"
                          title="Discard Draft"
                        >
                          {deletingId === sale.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <>
                              <XMarkIcon className="h-4 w-4" />
                              <span>Discard</span>
                            </>
                          )}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => navigate(`/retail-sales/${sale.id}`)}
                      className="text-blue-600 hover:text-blue-800 flex items-center space-x-1 text-xs"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>View</span>
                    </button>
                    {sale.status === 'completed' && (
                      <button
                        onClick={() => setDepositModalSale(sale)}
                        className="text-purple-600 hover:text-purple-800 flex items-center space-x-1 text-xs"
                        title="Mark Payment Deposited"
                      >
                        <CurrencyDollarIcon className="h-4 w-4" />
                        <span>Deposit</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sale #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Seller
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSales.map((sale) => (
                    <tr 
                      key={sale.id} 
                      className={`hover:bg-gray-50 ${sale.status === 'draft' ? 'bg-orange-50' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {sale.retail_sale_number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {new Date(sale.sale_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <div>{sale.seller_name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{sale.seller_mobile}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <span className="capitalize">{sale.sale_type === 'on_farm' ? 'On-Farm' : 'Market'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(sale.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                        ₹{getDisplayAmount(sale).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {sale.status === 'draft' && (
                            <>
                              <button
                                onClick={() => handleContinueDraft(sale)}
                                className="text-orange-600 hover:text-orange-800 flex items-center space-x-1"
                                title="Continue Editing"
                              >
                                <PencilIcon className="h-4 w-4" />
                                <span className="text-xs">Continue</span>
                              </button>
                              <button
                                onClick={() => handleDelete(sale.id, sale.retail_sale_number)}
                                disabled={deletingId === sale.id}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50 flex items-center space-x-1"
                                title="Discard Draft"
                              >
                                {deletingId === sale.id ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <>
                                    <XMarkIcon className="h-4 w-4" />
                                    <span className="text-xs">Discard</span>
                                  </>
                                )}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => navigate(`/retail-sales/${sale.id}`)}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {sale.status === 'completed' && (
                            <button
                              onClick={() => setDepositModalSale(sale)}
                              className="text-purple-600 hover:text-purple-800 flex items-center space-x-1"
                              title="Mark Payment Deposited"
                            >
                              <CurrencyDollarIcon className="h-4 w-4" />
                              <span className="text-xs">Deposit</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No sales found</p>
            <div className="mt-4 space-x-2">
              <Link to="/retail-sales/on-farm" className="btn-primary">
                Create On-Farm Sale
              </Link>
              <Link to="/retail-sales/market" className="btn-primary">
                Create Market Sale
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Payment Deposit Modal */}
      {depositModalSale && (
        <PaymentDepositModal
          sale={depositModalSale}
          isOpen={!!depositModalSale}
          onClose={() => setDepositModalSale(null)}
          onSuccess={() => {
            loadSales();
            setDepositModalSale(null);
          }}
        />
      )}
    </div>
  );
};

export default RetailSalesList;
